import { AppShell } from '@safepos/ui';
import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  AnnouncementBanner,
  LicenseBanner,
  SupportBanner,
  TrialBanner,
} from './components/Banners';
import { usePublicConfig } from './lib/license';
import { isGroup, type NavEntry, type NavItem } from './components/nav';
import { AuthProvider, PlatformAuthProvider, useAuth, usePlatformAuth } from './lib/auth';
import { ForgotPasswordPage, LoginPage, RegisterPage } from './pages/AuthPages';
import { CategoriesPage, ModifiersPage, ProductsPage } from './pages/CatalogPages';
import { CustomersPage, LoyaltyPage, PromotionsPage, VouchersPage } from './pages/CrmPages';
import { FnbSetupPage } from './pages/FnbPages';
import { ReportsPage } from './pages/ReportPages';
import { LabelsPage } from './pages/LabelsPage';
import { PurchasingPage, RecipesPage, StockOpsPage, StockPage } from './pages/InventoryPages';
import { ImportPage } from './pages/ImportPage';
import { AboutPage } from './pages/AboutPage';
import { AuditPage, DashboardPage, SecurityPage, SubscriptionPage } from './pages/InfoPages';
import { PaymentsPage } from './pages/PaymentPages';
import { OutletPricesPage, OutletSettingsPage } from './pages/OutletConfigPages';
import { ShiftsPage, TransactionsPage } from './pages/SalesPages';
import { DevicesPage, EmployeesPage, OutletsPage, RolesPage } from './pages/ManagePages';
import {
  AnnouncementsPage,
  HealthPage,
  PlansPage,
  PlatformAccountPage,
  PlatformForgotPage,
  PlatformLoginPage,
  TenantDetailPage,
  TenantsPage,
} from './pages/SuperAdminPages';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

const officeNav: NavEntry[] = [
  { to: '/office/dasbor', label: 'Dasbor', icon: 'Home2' },
  {
    label: 'Penjualan',
    icon: 'Cart',
    items: [
      { to: '/office/transaksi', label: 'Transaksi', perm: 'orders:read' },
      { to: '/office/shift', label: 'Shift Kasir', perm: 'reports:read' },
      { to: '/office/laporan', label: 'Laporan', perm: 'reports:read' },
    ],
  },
  {
    label: 'Katalog',
    icon: 'Tag',
    items: [
      { to: '/office/produk', label: 'Produk', perm: 'catalog:read' },
      { to: '/office/kategori', label: 'Kategori', perm: 'catalog:read' },
      { to: '/office/modifier', label: 'Modifier', perm: 'catalog:read' },
      { to: '/office/harga-outlet', label: 'Harga Outlet', perm: 'catalog:read' },
      { to: '/office/label', label: 'Label Barcode', perm: 'catalog:read' },
      {
        to: '/office/impor',
        label: 'Impor & Ekspor',
        perm: 'catalog:read',
        feature: 'catalog_import',
      },
    ],
  },
  {
    label: 'Inventori',
    icon: 'Box',
    items: [
      { to: '/office/stok', label: 'Stok', perm: 'inventory:read', feature: 'inventory' },
      {
        to: '/office/pembelian',
        label: 'Pembelian',
        perm: 'inventory:read',
        feature: 'purchasing',
      },
      {
        to: '/office/opname',
        label: 'Opname & Transfer',
        perm: 'inventory:read',
        feature: 'inventory',
      },
      { to: '/office/resep', label: 'Resep (BOM)', perm: 'inventory:read', feature: 'recipes' },
    ],
  },
  {
    label: 'Pelanggan & Promo',
    icon: 'Gift',
    items: [
      { to: '/office/pelanggan', label: 'Pelanggan', perm: 'customers:read', feature: 'crm' },
      { to: '/office/promo', label: 'Promo', perm: 'promotions:read', feature: 'promos' },
      { to: '/office/voucher', label: 'Voucher', perm: 'promotions:read', feature: 'vouchers' },
      { to: '/office/poin', label: 'Program Poin', perm: 'promotions:read', feature: 'loyalty' },
    ],
  },
  {
    label: 'Toko & Perangkat',
    icon: 'Shop',
    items: [
      { to: '/office/outlet', label: 'Outlet', perm: 'outlets:read' },
      { to: '/office/meja', label: 'Meja & Dapur', perm: 'settings:read', feature: 'tables' },
      { to: '/office/perangkat', label: 'Perangkat', perm: 'devices:read' },
      { to: '/office/pajak-struk', label: 'Pajak & Struk', perm: 'settings:read' },
      {
        to: '/office/pembayaran',
        label: 'Pembayaran',
        perm: 'settings:read',
        feature: 'payments_noncash',
      },
    ],
  },
  {
    label: 'Tim & Akses',
    icon: 'UsersGroupRounded',
    items: [
      { to: '/office/pegawai', label: 'Pegawai', perm: 'employees:read' },
      { to: '/office/peran', label: 'Peran & Izin', perm: 'roles:read' },
      { to: '/office/log', label: 'Log Aktivitas', perm: 'audit:read', feature: 'audit_log' },
    ],
  },
  {
    label: 'Akun',
    icon: 'Settings',
    items: [
      { to: '/office/langganan', label: 'Langganan', perm: 'subscription:read' },
      { to: '/office/keamanan', label: 'Keamanan Akun' },
      { to: '/office/tentang', label: 'Tentang & lisensi' },
    ],
  },
];

/** Sembunyikan item tanpa izin; kelompok tanpa item tersisa ikut disembunyikan. */
function visible(
  entries: NavEntry[],
  can: (p: string) => boolean,
  features: Record<string, boolean> = {},
): NavEntry[] {
  const ok = (i: NavItem) =>
    (!i.perm || can(i.perm)) && (!i.feature || features[i.feature] !== false);
  return entries.flatMap((e): NavEntry[] => {
    if (!isGroup(e)) return ok(e) ? [e] : [];
    const items = e.items.filter(ok);
    return items.length ? [{ ...e, items }] : [];
  });
}
function Brand({ title, mini }: { title: string; mini?: boolean }) {
  return (
    <span className="flex items-center gap-2">
      <img src="/mark-256.png" alt="" className="h-8 w-auto rounded-md bg-white p-1" />
      {!mini && <span className="wds-sidenav__logo">{title}</span>}
    </span>
  );
}

function Shell({
  title,
  items,
  user,
  onLogout,
  banners,
}: {
  title: string;
  items: NavEntry[];
  banners?: ReactNode;
  user: { name: string; roleName?: string };
  onLogout: () => void;
}) {
  return (
    <AppShell
      appName={title}
      logo={<Brand title={title} />}
      collapsedLogo={<Brand title={title} mini />}
      nav={items}
      user={user}
      onLogout={onLogout}
      banners={banners}
    />
  );
}

/** Instalasi self-hosted tidak berlangganan: menu "Langganan" menjadi "Lisensi". */
function withLicenseLabel(entries: NavEntry[], selfHosted: boolean): NavEntry[] {
  if (!selfHosted) return entries;
  const fix = (i: NavItem): NavItem =>
    i.to === '/office/langganan' ? { ...i, label: 'Lisensi' } : i;
  return entries.map((e) => (isGroup(e) ? { ...e, items: e.items.map(fix) } : fix(e)));
}

function OfficeLayout() {
  const { status, me, can, logout } = useAuth();
  const cfg = usePublicConfig();
  const loc = useLocation();
  if (status === 'loading') return <p className="p-8">Memuat…</p>;
  if (status === 'anonymous')
    return <Navigate to="/masuk" replace state={{ from: loc.pathname }} />;
  return (
    <Shell
      title="Back Office"
      items={withLicenseLabel(
        visible(officeNav, can, me?.features),
        cfg.data?.deployment === 'selfhosted',
      )}
      banners={
        <>
          <SupportBanner />
          <TrialBanner />
          <LicenseBanner />
          <AnnouncementBanner />
        </>
      }
      user={{ name: me?.user.name ?? '', roleName: me?.role.name }}
      onLogout={() => void logout()}
    />
  );
}

/** Route yang butuh izin tertentu; tanpa izin -> halaman 403 (API tetap menegakkan izin di server). */
function Guard({ perm, children }: { perm: string; children: React.ReactNode }) {
  const { can } = useAuth();
  if (!can(perm))
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Tidak punya akses</h1>
        <p className="text-slate-600">Anda tidak memiliki izin untuk halaman ini.</p>
      </div>
    );
  return <>{children}</>;
}

const adminNav: NavEntry[] = [
  { to: '/admin/kesehatan', label: 'Kesehatan', icon: 'ShieldCheck' },
  {
    label: 'Pelanggan',
    icon: 'UsersGroupRounded',
    items: [
      { to: '/admin/tenant', label: 'Tenant' },
      { to: '/admin/paket', label: 'Paket' },
    ],
  },
  { to: '/admin/pengumuman', label: 'Pengumuman', icon: 'Bell' },
  { to: '/admin/akun', label: 'Akun', icon: 'User' },
  { to: '/admin/tentang', label: 'Tentang & lisensi', icon: 'InfoCircle' },
];

function AdminLayout() {
  const { status, logout, admin } = usePlatformAuth();
  if (status === 'loading') return <p className="p-8">Memuat…</p>;
  if (status === 'anonymous') return <Navigate to="/admin/masuk" replace />;
  return (
    <Shell
      title="Super Admin"
      items={adminNav}
      user={{ name: admin?.email ?? 'Super Admin', roleName: 'Super Admin' }}
      onLogout={() => void logout()}
    />
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PlatformAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/masuk" element={<LoginPage />} />
              <Route path="/daftar" element={<RegisterPage />} />
              <Route path="/lupa-password" element={<ForgotPasswordPage />} />
              <Route element={<OfficeLayout />}>
                <Route path="/office/dasbor" element={<DashboardPage />} />
                <Route
                  path="/office/transaksi"
                  element={
                    <Guard perm="orders:read">
                      <TransactionsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/shift"
                  element={
                    <Guard perm="reports:read">
                      <ShiftsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/produk"
                  element={
                    <Guard perm="catalog:read">
                      <ProductsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/kategori"
                  element={
                    <Guard perm="catalog:read">
                      <CategoriesPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/modifier"
                  element={
                    <Guard perm="catalog:read">
                      <ModifiersPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/harga-outlet"
                  element={
                    <Guard perm="catalog:read">
                      <OutletPricesPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/impor"
                  element={
                    <Guard perm="catalog:read">
                      <ImportPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/pajak-struk"
                  element={
                    <Guard perm="settings:read">
                      <OutletSettingsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/outlet"
                  element={
                    <Guard perm="outlets:read">
                      <OutletsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/pegawai"
                  element={
                    <Guard perm="employees:read">
                      <EmployeesPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/peran"
                  element={
                    <Guard perm="roles:read">
                      <RolesPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/perangkat"
                  element={
                    <Guard perm="devices:read">
                      <DevicesPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/langganan"
                  element={
                    <Guard perm="subscription:read">
                      <SubscriptionPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/log"
                  element={
                    <Guard perm="audit:read">
                      <AuditPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/meja"
                  element={
                    <Guard perm="settings:read">
                      <FnbSetupPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/pembayaran"
                  element={
                    <Guard perm="settings:read">
                      <PaymentsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/stok"
                  element={
                    <Guard perm="inventory:read">
                      <StockPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/pembelian"
                  element={
                    <Guard perm="inventory:read">
                      <PurchasingPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/opname"
                  element={
                    <Guard perm="inventory:read">
                      <StockOpsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/resep"
                  element={
                    <Guard perm="inventory:read">
                      <RecipesPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/label"
                  element={
                    <Guard perm="catalog:read">
                      <LabelsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/laporan"
                  element={
                    <Guard perm="reports:read">
                      <ReportsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/pelanggan"
                  element={
                    <Guard perm="customers:read">
                      <CustomersPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/promo"
                  element={
                    <Guard perm="promotions:read">
                      <PromotionsPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/voucher"
                  element={
                    <Guard perm="promotions:read">
                      <VouchersPage />
                    </Guard>
                  }
                />
                <Route
                  path="/office/poin"
                  element={
                    <Guard perm="promotions:read">
                      <LoyaltyPage />
                    </Guard>
                  }
                />
                <Route path="/office/keamanan" element={<SecurityPage />} />
                <Route path="/office/tentang" element={<AboutPage />} />
              </Route>
              <Route path="/admin/masuk" element={<PlatformLoginPage />} />
              <Route path="/admin/lupa" element={<PlatformForgotPage />} />
              <Route path="/admin/atur-ulang" element={<PlatformForgotPage />} />
              <Route element={<AdminLayout />}>
                <Route path="/admin/kesehatan" element={<HealthPage />} />
                <Route path="/admin/tenant" element={<TenantsPage />} />
                <Route path="/admin/tenant/:id" element={<TenantDetailPage />} />
                <Route path="/admin/pengumuman" element={<AnnouncementsPage />} />
                <Route path="/admin/paket" element={<PlansPage />} />
                <Route path="/admin/akun" element={<PlatformAccountPage />} />
                <Route path="/admin/tentang" element={<AboutPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/office/dasbor" replace />} />
            </Routes>
          </BrowserRouter>
        </PlatformAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
